import React, { useCallback, useEffect, useState } from 'react';
import { fokusId } from '../lib/fokus';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, Download, Loader2, PhoneCall, Trash2, Siren, Mail, CheckCircle2, AlertCircle } from 'lucide-react';
import { api, useAuth, API } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const STATUSES = [
  { key: 'neu', label: 'NEU', cls: 'bg-rose-50 text-rose-700' },
  { key: 'kontaktiert', label: 'KONTAKTIERT', cls: 'bg-blue-50 text-blue-700' },
  { key: 'in_bearbeitung', label: 'IN BEARBEITUNG', cls: 'bg-[#b8893d]/15 text-[#b8893d]' },
  { key: 'erledigt', label: 'ERLEDIGT', cls: 'bg-green-50 text-green-700' },
];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const downloadFile = async (url, filename) => {
  const res = await api.get(url, { responseType: 'blob' });
  const href = URL.createObjectURL(res.data);
  const a = Object.assign(document.createElement('a'), { href, download: filename });
  a.click();
  URL.revokeObjectURL(href);
};

const NotfallCard = ({ item, onPatch, onDelete }) => {
  const [notiz, setNotiz] = useState(item.notiz || '');
  const st = STATUSES.find((s) => s.key === item.status) || STATUSES[0];
  const bm = item.bestand_match;
  return (
    <div className="bg-white border border-gray-100 rounded-sm shadow-sm p-5" data-testid={`notfall-card-${item.notfall_id}`} data-fokus={item.notfall_id}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] tracking-wider px-2 py-0.5 rounded-sm font-semibold ${st.cls}`}>{st.label}</span>
            <span className="text-[10px] tracking-wider px-2 py-0.5 rounded-sm bg-gray-100 text-gray-600">{{ bestand: 'BESTANDSMANDANT', behoerde: 'BEHÖRDE / GERICHT' }[item.modus] || 'NEUER MANDANT'}</span>
            {item.modus === 'behoerde' && <span className="text-[10px] tracking-wider px-2 py-0.5 rounded-sm bg-green-50 text-green-700">GEBÜHRENFREI</span>}
            {item.modus !== 'behoerde' && <span className={`text-[10px] tracking-wider px-2 py-0.5 rounded-sm ${item.paid ? 'bg-green-50 text-green-700' : 'bg-[#fff8e1] text-[#8a6d1a]'}`} data-testid={`notfall-paid-${item.notfall_id}`}>{item.paid ? 'PAUSCHALE BEZAHLT' : 'ZAHLUNG OFFEN'}</span>}
            <span className="text-xs text-rose-700 font-semibold">{item.notfallArt}</span>
          </div>
          <div className="font-serif text-xl text-[#1B3A5C] mt-1">{item.firstName} {item.lastName}{item.behoerde_name ? <span className="text-base text-[#7a8a99]"> · {item.behoerde_name}</span> : null}</div>
          <div className="text-xs text-[#7a8a99]">{item.notfall_id} · {fmt(item.created_at)}</div>
        </div>
        <select value={item.status} onChange={(e) => onPatch(item.notfall_id, { status: e.target.value })} data-testid={`notfall-status-${item.notfall_id}`}
          className="text-xs border border-gray-200 rounded-sm px-2 py-1.5 text-[#1B3A5C]">
          {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#34536b]">
        <div><span className="text-[#7a8a99]">Telefon:</span> <a href={`tel:${item.phone}`} className="text-[#1B3A5C] font-medium">{item.phone}</a></div>
        <div><span className="text-[#7a8a99]">E-Mail:</span> {item.email || '—'}</div>
        {item.street && <div className="md:col-span-2"><span className="text-[#7a8a99]">Adresse:</span> {item.street}, {item.zip} {item.city}</div>}
        {(item.mandanten_nr || item.aktenzeichen) && (
          <div className="md:col-span-2"><span className="text-[#7a8a99]">Angabe:</span> {item.mandanten_nr && `M-${item.mandanten_nr}`} {item.aktenzeichen && `· AZ ${item.aktenzeichen}`}
            {bm && (bm.found
              ? <span className="ml-2 inline-flex items-center gap-1 text-green-700 text-xs"><CheckCircle2 className="w-3 h-3" /> in Akte gefunden{bm.name ? `: ${bm.name}` : ''}</span>
              : <span className="ml-2 inline-flex items-center gap-1 text-amber-700 text-xs"><AlertCircle className="w-3 h-3" /> nicht gefunden – manuell prüfen</span>)}
          </div>
        )}
      </div>
      {item.beschreibung && <p className="mt-3 text-sm text-[#34536b] bg-[#f5f8fa] p-3 whitespace-pre-wrap">{item.beschreibung}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {item.ausweis_front && (
          <button type="button" onClick={() => downloadFile(`${API}/admin/notfallkontakte/${item.notfall_id}/ausweis/vorderseite`, `Ausweis_Vorderseite_${item.lastName}${item.ausweis_front.stored_filename.slice(item.ausweis_front.stored_filename.lastIndexOf('.'))}`)}
            className="inline-flex items-center gap-1 text-xs tracking-wider border border-[#1B3A5C] text-[#1B3A5C] px-3 py-1.5 rounded-sm hover:bg-[#1B3A5C] hover:text-white" data-testid={`notfall-ausweis-front-${item.notfall_id}`}>
            <Download className="w-3 h-3" /> AUSWEIS VORNE
          </button>
        )}
        {item.ausweis_back && (
          <button type="button" onClick={() => downloadFile(`${API}/admin/notfallkontakte/${item.notfall_id}/ausweis/rueckseite`, `Ausweis_Rueckseite_${item.lastName}${item.ausweis_back.stored_filename.slice(item.ausweis_back.stored_filename.lastIndexOf('.'))}`)}
            className="inline-flex items-center gap-1 text-xs tracking-wider border border-[#1B3A5C] text-[#1B3A5C] px-3 py-1.5 rounded-sm hover:bg-[#1B3A5C] hover:text-white">
            <Download className="w-3 h-3" /> AUSWEIS HINTEN
          </button>
        )}
        <span className="text-xs text-[#7a8a99] inline-flex items-center gap-1 ml-auto"><Mail className="w-3 h-3" /> Kanzlei-Mail: {item.email_sent == null ? 'ausstehend' : item.email_sent ? 'gesendet' : 'FEHLER'}</span>
        {onDelete && <button type="button" onClick={() => onDelete(item.notfall_id)} className="text-xs text-rose-600 inline-flex items-center gap-1 hover:underline" data-testid={`notfall-delete-${item.notfall_id}`}><Trash2 className="w-3 h-3" /> Löschen</button>}
      </div>
      <div className="mt-3 flex gap-2">
        <input value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="Interne Notiz …" className="flex-1 text-xs border border-gray-200 rounded-sm px-3 py-2" data-testid={`notfall-notiz-${item.notfall_id}`} />
        <button type="button" onClick={() => onPatch(item.notfall_id, { notiz })} className="text-xs tracking-wider bg-[#1B3A5C] text-white px-3 py-2 rounded-sm">SPEICHERN</button>
      </div>
      <p className="mt-3 text-[11px] text-[#9aa5b1]">{item.modus === 'behoerde' ? 'Bestätigt: Datenschutz (gebührenfrei, Behörde/Gericht)' : `Bestätigt: Honorar · AGB (Stand ${item.agb_version}) · Datenschutz · Widerrufsbelehrung · § 356 Abs. 4 BGB`} — {fmt(item.consented_at)}</p>
    </div>
  );
};

const AdminNotfall = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, isAdmin } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(fokusId() ? 'alle' : 'offen');

  const load = useCallback(async () => {
    try { setData((await api.get('/admin/notfallkontakte')).data); }
    catch (e) { toast({ title: 'Fehler', description: 'Notfallkontakte konnten nicht geladen werden.', variant: 'destructive' }); console.warn(e); }
  }, [toast]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !['admin', 'kanzlei_mitarbeiter'].includes(user.role)) { navigate('/login?next=/admin/notfallkontakte'); return; }
    load();
  }, [authLoading, user, navigate, load]);

  const patch = async (id, body) => {
    try { await api.patch(`/admin/notfallkontakte/${id}`, body); toast({ title: 'Gespeichert' }); load(); }
    catch (e) { toast({ title: 'Fehler', description: e?.response?.data?.detail || 'Speichern fehlgeschlagen', variant: 'destructive' }); }
  };
  const del = async (id) => {
    if (!window.confirm('Notfallkontakt inkl. Ausweisdateien endgültig löschen?')) return;
    try { await api.delete(`/admin/notfallkontakte/${id}`); toast({ title: 'Gelöscht' }); load(); }
    catch (e) { toast({ title: 'Fehler', description: 'Löschen fehlgeschlagen', variant: 'destructive' }); }
  };

  const items = (data?.items || []).filter((i) => (filter === 'offen' ? i.status !== 'erledigt' : filter === 'alle' ? true : i.status === 'erledigt'));

  return (
    <div className="min-h-screen bg-[#f5f8fa]">
      <Header />
      <main className="pt-[150px] pb-20">
        <section className="bg-[#1B3A5C] text-white py-10">
          <div className="max-w-6xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-3 text-[#e0b56a] text-xs tracking-[0.3em] mb-2"><span className="w-12 h-px bg-[#b8893d]"></span>NOTFALLHOTLINE</div>
            <h1 className="font-serif font-light text-3xl md:text-4xl tracking-tight flex items-center gap-3"><Siren className="w-8 h-8 text-[#b8893d]" /> Notfallkontakte {data && <span className="text-white/60 text-2xl">({data.total})</span>}</h1>
            {data?.hotline && <p className="text-white/70 text-sm mt-2 inline-flex items-center gap-2"><PhoneCall className="w-4 h-4" /> Hotline {data.hotline.display} · {data.hotline.pauschale} + {data.hotline.stundensatz}</p>}
          </div>
        </section>
        <section className="max-w-6xl mx-auto px-6 lg:px-10 mt-8">
          <div className="flex gap-2 mb-5" data-testid="notfall-filter">
            {[['offen', 'OFFEN'], ['erledigt', 'ERLEDIGT'], ['alle', 'ALLE']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setFilter(k)} className={`text-xs tracking-wider px-4 py-2 rounded-sm border ${filter === k ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]' : 'bg-white text-[#1B3A5C] border-gray-200'}`}>{l}</button>
            ))}
            {data && data.neu > 0 && <span className="ml-auto text-xs text-rose-700 font-semibold self-center" data-testid="notfall-neu-count">{data.neu} neu</span>}
          </div>
          {!data ? <div className="flex items-center gap-2 text-[#34536b] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Lade …</div>
            : items.length === 0 ? <p className="text-sm text-[#7a8a99] bg-white p-6 rounded-sm border border-gray-100" data-testid="notfall-empty">Keine Notfallkontakte in dieser Ansicht.</p>
            : <div className="space-y-4" data-testid="notfall-list">{items.map((i) => <NotfallCard key={i.notfall_id} item={i} onPatch={patch} onDelete={isAdmin ? del : null} />)}</div>}
        </section>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

export default AdminNotfall;
